import { X } from "lucide-react";
import Button from "../Button";
import "./_close-button.scss";

/**
 * Shared dismiss "x" for a modal/drawer/panel header.
 *
 * `variant="glass"` (default) - for anything sitting on the Liquid Glass
 * surface (ModalShell and its drawers: Market, Billing Unit, Usage Source,
 * Usage Compilation, Export, Company, ...). Pixel-sampled directly against
 * the live design file (companies-workspace.dc.html's "New market" drawer):
 * a ~28px square, `rgba(255,255,255,.5)` fill, `rgba(255,255,255,.72)`
 * border - exactly Button's own existing `--outlined`/`--xs` combo, so this
 * variant is nothing more than that combo pinned to icon-only.
 *
 * `variant="neutral"` - for the handful of pre-Liquid-Glass panels that
 * still sit on a flat opaque white/gray background (PdfModal,
 * WalletsInfoModal, IntegrationWizardModal, EventDetailDrawer) - the glass
 * variant's translucent white fill would go invisible there the same way
 * it did on ModalShell's own near-opaque content before that surface had
 * its warm/gradient tint. Bare icon, transparent at rest, light-gray hover.
 */
const CloseButton = ({ onClick, disabled, className = "", ariaLabel = "Close", size, variant = "glass" }) => {
  // For "glass", the rendered size is actually pinned by
  // _close-button.scss's `.button__content svg` override (10px) - Button's
  // own `.button--xs .button__content svg` CSS rule beats whatever's
  // passed here via the `size` prop (a plain HTML width/height attribute
  // on the <svg>, which CSS always wins over), so this value only matters
  // for "neutral", which has no such CSS override.
  const iconSize = size ?? (variant === "glass" ? 10 : 18);

  return (
    <Button
      type="button"
      variant={variant === "glass" ? "outlined" : "ghost"}
      size={variant === "glass" ? "xs" : undefined}
      iconOnly
      // A thinner stroke than lucide's default (2) - the design's own
      // cross reads as a fine line, not a bold one, at this small a size.
      icon={<X size={iconSize} strokeWidth={1.5} />}
      className={`close-button close-button--${variant} ${className}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
};

export default CloseButton;
